import { useEffect, useMemo, useState } from "react";
import { Lock } from "lucide-react";
import { PublicLayout } from "../../components/PublicLayout";
import { api } from "../../lib/api";
import { formatGlory } from "../../lib/adminDisplayLabels";

interface LevelSummary {
  member: null | {
    member_account: string;
    status_label: string;
  };
  assets: {
    glory_value: number;
  };
}

type GuildLevelDisplay = {
  level_number: number;
  display_name: string;
  glory_required: number;
  benefits: string;
  image_src: string;
  icon_url: string;
  is_enabled: boolean;
  sort_order: number;
  note: string;
  tone: string;
};

const levelSettings: GuildLevelDisplay[] = [
  {
    level_number: 1,
    display_name: "聚耀成員",
    glory_required: 0,
    benefits: "加入龍來喬公會，開啟基礎活動與會員身份顯示。",
    image_src: "/assets/guild-levels/lv1-juyao-member.png",
    icon_url: "/assets/guild-levels/lv1-juyao-member.png",
    is_enabled: true,
    sort_order: 1,
    note: "顯示設定",
    tone: "bronze"
  },
  {
    level_number: 2,
    display_name: "銅耀會員",
    glory_required: 1000,
    benefits: "累積活躍紀錄，作為後續會員權益參考。",
    image_src: "/assets/guild-levels/lv2-tongyao-member.png",
    icon_url: "/assets/guild-levels/lv2-tongyao-member.png",
    is_enabled: true,
    sort_order: 2,
    note: "顯示設定",
    tone: "copper"
  },
  {
    level_number: 3,
    display_name: "銀耀會員",
    glory_required: 3000,
    benefits: "提升公會身份，預留更高階會員活動資格。",
    image_src: "/assets/guild-levels/lv3-yinyao-member.png",
    icon_url: "/assets/guild-levels/lv3-yinyao-member.png",
    is_enabled: true,
    sort_order: 3,
    note: "顯示設定",
    tone: "silver"
  },
  {
    level_number: 4,
    display_name: "金耀會員",
    glory_required: 8000,
    benefits: "預留專屬活動、客服關懷與進階權益。",
    image_src: "/assets/guild-levels/lv4-jinyao-member.png",
    icon_url: "/assets/guild-levels/lv4-jinyao-member.png",
    is_enabled: true,
    sort_order: 4,
    note: "顯示設定",
    tone: "gold"
  },
  {
    level_number: 5,
    display_name: "鉑耀會員",
    glory_required: 15000,
    benefits: "預留高活躍會員加碼與身份標記。",
    image_src: "/assets/guild-levels/lv5-boyao-member.png",
    icon_url: "/assets/guild-levels/lv5-boyao-member.png",
    is_enabled: true,
    sort_order: 5,
    note: "顯示設定",
    tone: "platinum"
  },
  {
    level_number: 6,
    display_name: "皇耀會員",
    glory_required: 30000,
    benefits: "預留高階會員活動、節慶關懷與專屬資格。",
    image_src: "/assets/guild-levels/lv6-huangyao-member.png",
    icon_url: "/assets/guild-levels/lv6-huangyao-member.png",
    is_enabled: true,
    sort_order: 6,
    note: "顯示設定",
    tone: "royal"
  },
  {
    level_number: 7,
    display_name: "鑽耀貴賓",
    glory_required: 60000,
    benefits: "預留貴賓活動與人工審核優先參考。",
    image_src: "/assets/guild-levels/lv7-zuanyao-vip.png",
    icon_url: "/assets/guild-levels/lv7-zuanyao-vip.png",
    is_enabled: true,
    sort_order: 7,
    note: "顯示設定",
    tone: "diamond"
  },
  {
    level_number: 8,
    display_name: "星耀貴賓",
    glory_required: 100000,
    benefits: "預留星耀級會員專屬權益與進階活動入口。",
    image_src: "/assets/guild-levels/lv8-xingyao-vip.png",
    icon_url: "/assets/guild-levels/lv8-xingyao-vip.png",
    is_enabled: true,
    sort_order: 8,
    note: "顯示設定",
    tone: "stellar"
  },
  {
    level_number: 9,
    display_name: "尊耀公爵",
    glory_required: 180000,
    benefits: "預留高階會員關懷、特別活動與身份展示。",
    image_src: "/assets/guild-levels/lv9-zunyao-duke.png",
    icon_url: "/assets/guild-levels/lv9-zunyao-duke.png",
    is_enabled: true,
    sort_order: 9,
    note: "顯示設定",
    tone: "duke"
  },
  {
    level_number: 10,
    display_name: "龍耀公爵",
    glory_required: 300000,
    benefits: "預留最高公會身份與頂級權益設定。",
    image_src: "/assets/guild-levels/lv10-longyao-duke.png",
    icon_url: "/assets/guild-levels/lv10-longyao-duke.png",
    is_enabled: true,
    sort_order: 10,
    note: "顯示設定",
    tone: "dragon"
  }
];

interface LevelSettingsResponse {
  settings: unknown;
}

export function LevelPage() {
  const account = localStorage.getItem("current_member_account") ?? localStorage.getItem("memberAccount") ?? "";
  const [summary, setSummary] = useState<LevelSummary | null>(null);
  const [settings, setSettings] = useState<GuildLevelDisplay[]>(levelSettings);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!account) return;
    api<LevelSummary>(`/api/member/summary?member_account=${encodeURIComponent(account)}`)
      .then(setSummary)
      .catch((err) => setError(err instanceof Error ? err.message : "讀取會員等級資料失敗"));
    api("/api/public/weekly-pass/mark-level-view", {
      method: "POST",
      body: JSON.stringify({ member_account: account })
    }).catch(() => undefined);
  }, [account]);

  useEffect(() => {
    api<LevelSettingsResponse | null>("/api/public/level-settings")
      .then((data) => {
        const normalized = normalizeLevelSettings(data?.settings);
        if (normalized.length) setSettings(normalized);
      })
      .catch(() => setSettings(levelSettings));
  }, []);

  const gloryValue = summary?.assets?.glory_value ?? 0;
  const levelInfo = getLevelInfo(gloryValue, settings);
  const progress = useMemo(() => {
    if (!levelInfo.next) return 100;
    const start = levelInfo.current.glory_required;
    const end = levelInfo.next.glory_required;
    const span = Math.max(1, end - start);
    return Math.max(0, Math.min(100, Math.round(((gloryValue - start) / span) * 100)));
  }, [gloryValue, levelInfo.current.glory_required, levelInfo.next]);

  return (
    <PublicLayout title="會員身份" subtitle="龍來喬公會等級與閃耀值">
      {summary?.member ? (
        <>
          <section className="level-vip-hero">
            <div className="level-vip-bg" />
            <div className={`level-hero-emblem ${levelInfo.current.tone}`} aria-label="目前等級徽章">
              <div className="level-emblem-image-slot">
                <img src={levelInfo.current.image_src} alt={`Lv${levelInfo.current.level_number} ${levelInfo.current.display_name}`} />
              </div>
            </div>
            <div className="level-vip-title">
              <span>GUILD RANK</span>
              <h1>Lv{levelInfo.current.level_number} {levelInfo.current.display_name}</h1>
              <p>目前公會身份徽章</p>
            </div>
          </section>

          <section className="level-info-card level-game-info">
            <span className="hero-kicker">閃耀值</span>
            <h2>閃耀值說明</h2>
            <p>閃耀值代表你在龍來喬的活躍累積，累積越高，可解鎖更高會員身份。</p>
            <p>達到指定閃耀值後，將升級至下一階會員身份。</p>
          </section>

          <section className="level-overview-card level-game-overview">
            <div className="level-overview-glow" />
            <div className="level-overview-head">
              <span>目前等級</span>
              <strong>Lv{levelInfo.current.level_number} {levelInfo.current.display_name}</strong>
            </div>
            <div className="level-overview-stats">
              <div>
                <span>目前閃耀值</span>
                <b>{formatGlory(gloryValue)}</b>
              </div>
              <div>
                <span>下一級</span>
                <b>{levelInfo.next ? `Lv${levelInfo.next.level_number} ${levelInfo.next.display_name}` : "已達最高等級"}</b>
              </div>
              <div>
                <span>距離下一級</span>
                <b>{levelInfo.next ? `還差 ${formatGlory(levelInfo.remainingToNext)}` : "已完成所有等級"}</b>
              </div>
            </div>
            <div className="level-progress-wrap" aria-label="升級進度">
              <div className="level-progress-label">
                <span>Lv{levelInfo.current.level_number}</span>
                <span>{levelInfo.next ? `Lv${levelInfo.next.level_number}` : "MAX"}</span>
              </div>
              <div className="level-progress-track">
                <div className="level-progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </section>

          <section className="level-carousel-section level-game-carousel">
            <div className="section-title-row">
              <h2>公會段位卡</h2>
              <span>Lv{levelInfo.current.level_number} / Lv10</span>
            </div>
            <div className="level-card-carousel" aria-label="左右滑動查看等級卡">
              {settings.map((level) => (
                <LevelCard
                  key={level.level_number}
                  level={level}
                  currentLevel={levelInfo.current.level_number}
                  nextLevel={levelInfo.next?.level_number ?? null}
                  remainingToNext={levelInfo.remainingToNext}
                />
              ))}
            </div>
            <div className="level-carousel-indicator" aria-hidden="true">
              {settings.map((level) => (
                <span
                  key={level.level_number}
                  className={level.level_number === levelInfo.current.level_number ? "active" : level.level_number === levelInfo.next?.level_number ? "next" : ""}
                />
              ))}
            </div>
            <p className="level-game-note">公會等級目前為展示版，正式升級規則與權益以後台設定及官方公告為準。</p>
          </section>
        </>
      ) : (
        <section className="login-gate">
          <span className="hero-kicker">公會等級</span>
          <h1>請先登入查看會員身份</h1>
          <p>{error || "登入後即可查看公會等級、閃耀值與下一級目標。"}</p>
          <a className="primary-cta" href="/">回到登入入口</a>
        </section>
      )}
    </PublicLayout>
  );
}

function LevelCard({
  level,
  currentLevel,
  nextLevel,
  remainingToNext
}: {
  level: GuildLevelDisplay;
  currentLevel: number;
  nextLevel: number | null;
  remainingToNext: number;
}) {
  const isCurrent = level.level_number === currentLevel;
  const isNext = level.level_number === nextLevel;
  const isLocked = level.level_number > currentLevel && !isNext;
  const isUnlocked = level.level_number < currentLevel;
  const statusLabel = isCurrent ? "目前等級" : isNext ? "下一級" : isLocked ? "尚未解鎖" : "已解鎖";

  return (
    <article className={`level-rank-card level-vip-card ${level.tone} ${isCurrent ? "current" : ""} ${isNext ? "next" : ""} ${isLocked ? "locked" : ""} ${isUnlocked ? "unlocked" : ""}`}>
      <div className="level-card-shine" />
      <div className="level-rank-orb level-card-emblem">
        <img src={level.image_src} alt={`Lv${level.level_number} ${level.display_name}`} />
        {isLocked ? <span className="level-lock-overlay"><Lock size={20} /></span> : null}
      </div>
      <div className="level-rank-top">
        <span>Lv{level.level_number}</span>
        <h3>{level.display_name}</h3>
      </div>
      <div className="level-rank-body">
        <p><b>所需閃耀值：</b>{formatGlory(level.glory_required)}</p>
        <p>{level.benefits}</p>
        {isNext ? <strong>再累積 {formatGlory(remainingToNext)} 閃耀值即可升級</strong> : null}
      </div>
      <span className={`level-rank-badge ${isCurrent ? "current" : isNext ? "next" : isLocked ? "locked" : "done"}`}>
        {statusLabel}
      </span>
    </article>
  );
}

function getLevelInfo(gloryValue: number | null | undefined, source: GuildLevelDisplay[]) {
  const value = Math.max(0, Number(gloryValue ?? 0));
  const enabled = source.filter((level) => level.is_enabled).sort((a, b) => a.glory_required - b.glory_required);
  const levels = enabled.length ? enabled : levelSettings;
  const current = levels.reduce((best, level) => value >= level.glory_required ? level : best, levels[0]);
  const next = levels.find((level) => level.glory_required > value) ?? null;
  return {
    gloryValue: value,
    current,
    next,
    remainingToNext: next ? Math.max(0, next.glory_required - value) : 0,
    isMaxLevel: !next
  };
}

function normalizeLevelSettings(input: unknown): GuildLevelDisplay[] {
  if (!Array.isArray(input)) return [];
  const rows = input.map((item, index) => {
    const raw = item && typeof item === "object" ? item as Record<string, unknown> : {};
    const levelNumber = Number(raw.level_number);
    const fallback = levelSettings.find((level) => level.level_number === levelNumber) ?? levelSettings[index];
    if (!fallback || !Number.isInteger(levelNumber) || levelNumber < 1 || levelNumber > 10) return null;
    const iconUrl = String(raw.icon_url ?? fallback.icon_url);
    return {
      level_number: levelNumber,
      display_name: String(raw.display_name ?? fallback.display_name),
      glory_required: Math.max(0, Number(raw.glory_required ?? fallback.glory_required)),
      benefits: String(raw.benefits ?? fallback.benefits),
      icon_url: iconUrl.startsWith("/assets/guild-levels/") ? iconUrl : fallback.icon_url,
      image_src: iconUrl.startsWith("/assets/guild-levels/") ? iconUrl : fallback.icon_url,
      is_enabled: raw.is_enabled === false ? false : true,
      sort_order: Number(raw.sort_order ?? fallback.sort_order),
      note: String(raw.note ?? fallback.note),
      tone: fallback.tone
    };
  }).filter(Boolean) as GuildLevelDisplay[];
  return rows.length === 10 ? rows.sort((a, b) => a.sort_order - b.sort_order || a.level_number - b.level_number) : [];
}
